// src/Components/HighLevelComponents/DocumentUploader/DocumentUploader.tsx
import React, { useState, useEffect, useContext } from "react";
import {
  FaFileUpload,
  FaFilePdf,
  FaFileWord,
  FaFileAlt,
  FaTrash,
} from "react-icons/fa";
import { GoogleAnalyticsContext } from "../../../Hooks/GoogleAnalyticsContext";
import toast from "react-hot-toast";
import * as pdfjsLib from "pdfjs-dist";
import * as mammoth from "mammoth";

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface DocumentUploaderProps {
  onContentExtracted: (content: string) => void;
  isLoading?: boolean;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onContentExtracted,
  isLoading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedContent, setExtractedContent] = useState("");
  const [error, setError] = useState("");
  const { logEvent } = useContext(GoogleAnalyticsContext);

  // Clear the document and extracted content
  const clearDocument = () => {
    setFile(null);
    setExtractedContent("");
    setError("");
    logEvent("DocumentUpload", "Clear", "document_uploader");
  };

  // Process file when it's uploaded
  useEffect(() => {
    if (!file) return;
    processFile();
  }, [file]);

  // When extracted content changes, call the parent callback
  useEffect(() => {
    if (extractedContent) {
      onContentExtracted(extractedContent);
    }
  }, [extractedContent]);

  // Determine file type and call appropriate parser
  const processFile = async () => {
    if (!file) return;

    setProcessing(true);
    setProgress(10);
    setError("");

    try {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      let content = "";

      switch (fileExtension) {
        case "pdf":
          content = await processPdf(file);
          logEvent("DocumentUpload", "ProcessPDF", file.name);
          break;
        case "docx":
          content = await processDocx(file);
          logEvent("DocumentUpload", "ProcessDOCX", file.name);
          break;
        case "doc":
          setError(
            "Legacy .doc files are not supported. Please convert to .docx and try again."
          );
          logEvent("DocumentUpload", "Error", "unsupported_doc_format");
          break;
        case "txt":
          content = await processTxt(file);
          logEvent("DocumentUpload", "ProcessTXT", file.name);
          break;
        case "csv":
          content = await processCsv(file);
          logEvent("DocumentUpload", "ProcessCSV", file.name);
          break;
        case "xlsx":
        case "xls":
          content = await processExcel(file);
          logEvent("DocumentUpload", "ProcessExcel", file.name);
          break;
        default:
          setError(`Unsupported file type: .${fileExtension}`);
          logEvent(
            "DocumentUpload",
            "Error",
            `unsupported_file_format_${fileExtension}`
          );
          break;
      }

      if (content) {
        setExtractedContent(content);
        setProgress(100);
        toast.success(`Document processed successfully: ${file.name}`);
      }
    } catch (err) {
      console.error("Error processing file:", err);
      setError(
        `Failed to process file: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
      logEvent("DocumentUpload", "Error", `processing_error_${file.name}`);
      toast.error("Failed to process document");
    } finally {
      setProcessing(false);
    }
  };

  // Process PDF files
  const processPdf = async (file: File): Promise<string> => {
    setProgress(30);

    try {
      // Read the file as ArrayBuffer
      const arrayBuffer = await readFileAsArrayBuffer(file);
      setProgress(50);

      // Load the PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setProgress(60);

      let fullText = "";

      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(" ");
        fullText += pageText + "\n";
        setProgress(60 + (i / pdf.numPages) * 20);
      }

      setProgress(80);
      return fullText;
    } catch (error) {
      throw new Error(`PDF processing failed: ${error}`);
    }
  };

  // Process DOCX files
  const processDocx = async (file: File): Promise<string> => {
    setProgress(30);

    try {
      // Read the file as ArrayBuffer
      const arrayBuffer = await readFileAsArrayBuffer(file);
      setProgress(50);

      // Use mammoth to extract text
      const result = await mammoth.extractRawText({ arrayBuffer });
      setProgress(80);

      // Return the extracted text
      return result.value;
    } catch (error) {
      throw new Error(`DOCX processing failed: ${error}`);
    }
  };

  // Process TXT files
  const processTxt = async (file: File): Promise<string> => {
    setProgress(30);

    try {
      const text = await readFileAsText(file);
      setProgress(90);
      return text;
    } catch (error) {
      throw new Error(`TXT processing failed: ${error}`);
    }
  };

  // Process CSV files
  const processCsv = async (file: File): Promise<string> => {
    setProgress(30);

    try {
      const text = await readFileAsText(file);
      setProgress(50);

      // Process CSV - in production this would use PapaParse
      // For now, just return the raw text
      return text;
    } catch (error) {
      throw new Error(`CSV processing failed: ${error}`);
    }
  };

  // Process Excel files
  const processExcel = async (file: File): Promise<string> => {
    setProgress(30);

    try {
      // Read the file as ArrayBuffer
      const arrayBuffer = await readFileAsArrayBuffer(file);
      setProgress(50);

      // In a production environment, you would use a library like SheetJS
      // For now, return a message indicating this is a placeholder
      return `Content extracted from Excel: ${file.name}\n\nThis is a placeholder for Excel processing. In production, this would use the SheetJS library to extract data from the spreadsheet.`;
    } catch (error) {
      throw new Error(`Excel processing failed: ${error}`);
    }
  };

  // Utility function to read file as text
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (!e.target || typeof e.target.result !== "string") {
          reject(new Error("Failed to read file"));
          return;
        }

        resolve(e.target.result);
      };

      reader.onerror = () => reject(new Error("Error reading file"));
      reader.readAsText(file);
    });
  };

  // Utility function to read file as ArrayBuffer
  const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (!e.target || !e.target.result) {
          reject(new Error("Failed to read file"));
          return;
        }

        resolve(e.target.result as ArrayBuffer);
      };

      reader.onerror = () => reject(new Error("Error reading file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      logEvent(
        "DocumentUpload",
        "Select",
        `${selectedFile.name}_${selectedFile.type}`
      );
    }
  };

  // Get file icon based on file type
  const getFileIcon = () => {
    if (!file) return <FaFileUpload className="w-8 h-8 text-gray-400" />;

    const extension = file.name.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <FaFilePdf className="w-8 h-8 text-red-500" />;
      case "doc":
      case "docx":
        return <FaFileWord className="w-8 h-8 text-blue-500" />;
      default:
        return <FaFileAlt className="w-8 h-8 text-gray-500" />;
    }
  };

  // Handle drag and drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
      logEvent(
        "DocumentUpload",
        "DropFile",
        `${droppedFile.name}_${droppedFile.type}`
      );
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="w-full">
      {!file ? (
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-yellow-400 transition-colors cursor-pointer"
          onClick={() => document.getElementById("file-upload")?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls"
            disabled={isLoading || processing}
          />

          <div className="flex flex-col items-center justify-center">
            <FaFileUpload className="w-10 h-10 mb-3 text-gray-400" />
            <p className="text-gray-700 mb-1">
              Drag and drop a file here, or click to select
            </p>
            <p className="text-xs text-gray-500">
              Supported formats: PDF, DOCX, TXT, CSV, XLSX
            </p>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {getFileIcon()}
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {Math.round(file.size / 1024)} KB
                </p>
              </div>
            </div>

            <button
              onClick={clearDocument}
              className="text-gray-500 hover:text-red-500 transition-colors"
              disabled={isLoading || processing}
              aria-label="Remove document"
            >
              <FaTrash />
            </button>
          </div>

          {processing && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-yellow-400 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Processing document...
              </p>
            </div>
          )}

          {error && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {error}
            </div>
          )}

          {extractedContent && !processing && !error && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-600">
              Document processed successfully! {extractedContent.length}{" "}
              characters extracted.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
